import React, {
  useMemo,
  useCallback,
  memo
} from 'react';
import { View, Text, Image, TouchableOpacity, Platform } from 'react-native';
import styles from '@stylesheet';
import AvatarHandler from '@components/Global/AvatarHandler/AvatarHandler';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

const LIST_MODE = {
  DEFAULT: 'FLATLIST',
  FLATLIST: 'FLATLIST',
  SCROLLVIEW: 'SCROLLVIEW',
  MODAL: 'MODAL'
}

function RenderedItem({
  rtl,
  item,
  label,
  value,
  parent,
  selectable,
  disabled,
  props,
  labelProps,
  custom,
  isSelected,
  IconComponent,
  TickIconComponent,
  listItemContainerStyle,
  listItemLabelStyle,
  listChildContainerStyle,
  listParentContainerStyle,
  listChildLabelStyle,
  listParentLabelStyle,
  customItemContainerStyle,
  customItemLabelStyle,
  selectedItemContainerStyle,
  selectedItemLabelStyle,
  disabledItemContainerStyle,
  disabledItemLabelStyle,
  containerStyle,
  labelStyle,
  categorySelectable,
  onPress,
  setPosition,
  THEME,
  isMultiSelect,
  selectedValues
}) {
  /**
  * The tick icon component.
  * @returns {JSX|null}
  */
  // For multi-select, show checkbox instead of tick icon
  const isChecked = isMultiSelect && selectedValues && Array.isArray(selectedValues) && selectedValues.includes(value);

  const _TickIconComponent = useMemo(() => {
    if (isMultiSelect) {
      return (
        <View style={{
          width: 22,
          height: 22,
          borderRadius: 6,
          borderWidth: 2,
          borderColor: isChecked ? '#3b82f6' : '#cbd5e1',
          backgroundColor: isChecked ? '#3b82f6' : 'transparent',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          {isChecked && (
            <Text style={{ color: 'white', fontSize: 14, fontWeight: 'bold' }}>✓</Text>
          )}
        </View>
      );
    }
    return isSelected && (
      <View style={{ alignSelf: 'center', justifyContent: 'center' }}>
        <TickIconComponent />
      </View>
    );
  }, [isSelected, TickIconComponent, isMultiSelect, isChecked, value, selectedValues]);

  /**
   * The list category container style.
   * @returns {object}
   */
  const _listParentChildContainerStyle = useMemo(() => (parent !== null ? [
    THEME.listChildContainer,
    ...[listChildContainerStyle].flat()
  ] : [
    THEME.listParentContainer,
    ...[listParentContainerStyle].flat()
  ]), [THEME, rtl, listChildContainerStyle, listParentContainerStyle, parent])

  /**
   * The selected item container style.
   * @returns {object}
   */
  const _selectedItemContainerStyle = useMemo(() => isSelected && selectedItemContainerStyle, [isSelected, selectedItemContainerStyle]);

  /**
   * The disabled item container style.
   * @returns {object}
   */
  const _disabledItemContainerStyle = useMemo(() => disabled && disabledItemContainerStyle, [disabled, disabledItemContainerStyle]);

  /**
   * The custom container item style.
   * @returns {JSX}
   */
  const _customItemContainerStyle = useMemo(() => custom && ([
    THEME.customItemContainer,
    ...[customItemContainerStyle].flat()
  ]), [THEME, custom, customItemContainerStyle]);

  /**
   * The list item container style.
   * @returns {object}
   */
  const _listItemContainerStyle = useMemo(() => ([
    ...[listItemContainerStyle].flat(),
    ...[_listParentChildContainerStyle].flat(),
    ...[containerStyle].flat(),
    ...[_selectedItemContainerStyle].flat(),
    ...[_customItemContainerStyle].flat(),
    ...[_disabledItemContainerStyle].flat(),
  ]), [listItemContainerStyle, _listParentChildContainerStyle, _selectedItemContainerStyle, _customItemContainerStyle, _disabledItemContainerStyle, containerStyle]);

  /**
   * The list category label style.
   * @returns {object}
   */
  const _listParentChildLabelStyle = useMemo(() => (parent !== null ? [
    THEME.listChildLabel,
    ...[listChildLabelStyle].flat(),
  ] : [
    THEME.listParentLabel,
    ...[listParentLabelStyle].flat(),
  ]), [THEME, listChildLabelStyle, listParentLabelStyle, parent]);

  /**
   * The selected item label style.
   * @returns {object}
   */
  const _selectedItemLabelStyle = useMemo(() => isSelected && selectedItemLabelStyle, [isSelected, selectedItemLabelStyle]);

  /**
   * The disabled item label style.
   * @returns {object}
   */
  const _disabledItemLabelStyle = useMemo(() => disabled && disabledItemLabelStyle, [disabled, disabledItemLabelStyle]);

  /**
   * The custom label item style.
   * @returns {JSX}
   */
  const _customItemLabelStyle = useMemo(() => custom && ([
    THEME.customItemLabel,
    ...[customItemLabelStyle].flat()
  ]), [THEME, custom, customItemLabelStyle]);

  /**
   * The list item label style.
   * @returns {object}
   */
  const _listItemLabelStyle = useMemo(() => ([
    ...[listItemLabelStyle].flat(),
    ...[_listParentChildLabelStyle].flat(),
    ...[labelStyle].flat(),
    ...[_selectedItemLabelStyle].flat(),
    ...[_customItemLabelStyle].flat(),
    ...[_disabledItemLabelStyle].flat(),
  ]), [listItemLabelStyle, _listParentChildLabelStyle, _selectedItemLabelStyle, _customItemLabelStyle, _disabledItemLabelStyle, labelStyle]);

  /**
   * onPress.
   */
  const __onPress = useCallback(() => {
    if (disabled) return;
    onPress(item, custom);
  }, [onPress, item, custom, disabled]);

  /**
   * onLayout.
   */
  const onLayout = useCallback(({ nativeEvent: { layout: { y } } }) => {
    setPosition(value, y);
  }, [value]);

  const isWeb = Platform.OS === 'web';
  const theme = themeSelector();
  const [primaryLabel, secondaryLabel] = String(label || '').split('\n');

  return (
    <TouchableOpacity 
      {...props}
      style={[
        _listItemContainerStyle, 
        { 
          width: '100%',
          alignSelf: 'stretch',
          marginVertical: 0,
          minHeight: 60,
          height: 'auto',
          borderRadius: 0,
          overflow: 'hidden',
          backgroundColor: isSelected ? (theme ? '#eff6ff' : '#1e3a8a') : 'transparent',
        }
      ]} 
      onPress={__onPress} 
      onLayout={onLayout} 
      disabled={selectable === false || disabled} 
      testID={item.testID}
      activeOpacity={0.7}
    >
      {IconComponent}
      <View style={{ 
        flexDirection: 'row',
        flex: 1,
        alignItems: 'center',
        minWidth: '100%',
        paddingHorizontal: 12,
        paddingVertical: 12,
      }}>
        <View
          style={{
            width: 44,
            height: 44,
            justifyContent: 'center',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <AvatarHandler 
            avatar={item.avatar} 
            avatarImageStyle={{ width: 36, height: 36, borderRadius: 18 }}
            replacementImageStyle={{ width: 36, height: 36, borderRadius: 18 }}
          />
        </View>
        <View
          style={{
            flexGrow: 1,
            flexShrink: 1,
            flexBasis: 0,
            width: 0,
            top: 5,
            marginLeft: 12,
            marginRight: 4,
            justifyContent: 'center',
            minHeight: 44,
            alignSelf: 'stretch',
          }}
        >
          <Text
            numberOfLines={1}
            ellipsizeMode="tail"
            style={[
              _listItemLabelStyle,
              {
                fontSize: 14,
                fontWeight: isSelected ? '600' : '500',
                color: theme ? '#1e293b' : '#f8fafc',
                lineHeight: 17,
                flexShrink: 1,
                width: '100%',
              }
            ]}
            {...labelProps}
          >
            {primaryLabel}
          </Text>
          {!!secondaryLabel && (
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              style={{
                top: -10,
                fontSize: 12,
                lineHeight: 14,
                color: theme ? '#64748b' : '#cbd5e1',
                flexShrink: 1,
                width: '100%',
              }}
            >
              {secondaryLabel}
            </Text>
          )}
        </View>
        <View
          style={{
            width: isSelected ? 22 : 8,
            flexShrink: 0,
            alignItems: 'flex-end',
            justifyContent: 'center',
            marginLeft: 4,
          }}
        >
          {_TickIconComponent}
        </View>
      </View>
      <View
        pointerEvents="none"
        style={{
          height: 1,
          marginLeft: 64,
          marginRight: 12,
          backgroundColor: theme ? '#e2e8f0' : '#334155',
          opacity: 0.9,
        }}
      />
    </TouchableOpacity>
  )
}

const areEqual = (nextProps, prevProps) => {
  if (nextProps.label !== prevProps.label)
    return false;
  if (nextProps.value !== prevProps.value)
    return false;
  if (nextProps.parent !== prevProps.parent)
    return false;
  if (nextProps.selectable !== prevProps.selectable)
    return false;
  if (nextProps.disabled !== prevProps.disabled)
    return false;
  if (nextProps.custom !== prevProps.custom)
    return false;
  if (nextProps.isSelected !== prevProps.isSelected)
    return false;
  if (nextProps.categorySelectable !== prevProps.categorySelectable)
    return false;
  if (nextProps.rtl !== prevProps.rtl)
    return false;
  if (nextProps.theme !== prevProps.theme)
    return false;

  return true;
}

export default memo(RenderedItem, areEqual);